import React, { useEffect } from 'react';
import Table from 'Components/Table';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { RoleService } from 'Services';
import useStickyTableHead from 'Hooks/useStickyTableHead';
import useSplitviewForm from 'Components/Table/hooks/useSplitViewForm';
import useGetRuleQuickFilters from 'Hooks/rule-quickfilters';
import { QF_MODIFIED, QF_STATUS } from 'Hooks/rule-quickfilters/helper';

import { useQueryParam } from 'Hooks/useQueryParam';
import SplitViewContent from './SplitViewContent';

import styles from './TenantGroupRulesTable.css';

type Mode = 'sandbox' | 'deployed' | 'revisionWindow';

type Props = {
    globalRules: GlobalRulesData;
    tenantOverrideRules: GlobalRulesData;
    columnNames: Column<Rule>[];
    mode: Mode;
    tableName?: string;
    isLoading?: boolean;
};

const TABLEID = 'tenant-group-rules-overrides-table';

const TenantGroupRulesTable = ({
    globalRules,
    tenantOverrideRules,
    columnNames,
    mode,
    tableName,
    isLoading = false
}: Props) => {
    const { t } = useTranslation();
    useStickyTableHead(TABLEID);
    const columns = React.useMemo<Column<Rule>[]>(() => columnNames, [columnNames]);
    const {
        splitViewFormState,
        setIsOpen,
        setInitialFormState,
        setRowId
    } = useSplitviewForm<MerchantRuleSplitViewFormState>();
    const readOnly = mode === 'deployed' || mode === 'revisionWindow';
    const updateSplitViewId = React.useCallback(
        (val) => {
            setRowId?.(val);
            setIsOpen?.(false);
        },
        [setRowId, setIsOpen]
    );

    const quickFilters = useGetRuleQuickFilters({
        quickFilters: mode === 'deployed' ? [QF_STATUS.id] : [QF_MODIFIED.id, QF_STATUS.id],
        initialFilters: [
            {
                id: QF_STATUS.id,
                activeId: readOnly ? [QF_STATUS.options.active] : []
            }
        ]
    });

    const {
        parsed: { selectedRowId }
    } = useQueryParam();

    useEffect(() => {
        setRowId?.(selectedRowId);
    }, [selectedRowId, setRowId]);

    const data = React.useMemo(
        () =>
            tenantOverrideRules?.rules.map((rule) => {
                return {
                    ...rule,
                    overrideGroup: t('Overrides')
                };
            }) || [],
        [tenantOverrideRules, t]
    );

    return (
        <div className={styles.tableContainer}>
            <Table
                tableName={tableName}
                isLoading={isLoading}
                showMultiSort
                showRichFilter
                manualSplitView
                controlledSplitViewId={splitViewFormState.rowId}
                updateSplitViewId={updateSplitViewId}
                showExpandableSearch
                icon={{
                    showIcon: true
                }}
                expand={{
                    canExpand: true,
                    expandAll: true
                }}
                initialState={{
                    expanded: { 'overrideGroup:Overrides': true },
                    groupBy: ['overrideGroup'],
                    sortBy: [{ id: 'id', desc: false }],
                    globalFilter: {
                        quickFilters: quickFilters.initialQuickFilters
                    }
                }}
                dataTestId={TABLEID}
                columns={columns}
                noDataMessage='noTenantGroupRules'
                data={data}
                clickableRow
                splitViewContent={(props) => (
                    <SplitViewContent
                        setRowId={setRowId}
                        setInitialFormState={setInitialFormState}
                        splitViewFormState={splitViewFormState}
                        setIsOpen={setIsOpen}
                        dataTestId='tenantGroup'
                        globalRules={globalRules}
                        tenantGroupId={tenantOverrideRules.tenantGroupId}
                        readOnly={readOnly || !RoleService.hasEditRuleOrBulkUploadAccess}
                        mode={mode}
                        {...props}
                    />
                )}
                quickFilters={quickFilters.filters}
            />
        </div>
    );
};

export default TenantGroupRulesTable;
